<template>
    <v-row>
        <!-- Statuses -->
        <v-col cols="12" :md="statuses && statuses.length > 0 ? 12 : 12">
            <div class="text-subtitle-2 mb-2 text-grey">
                {{ title }}
                <v-progress-circular v-if="!statuses" indeterminate size="16" width="2" class="ml-2"
                    color="grey"></v-progress-circular>
            </div>

            <!-- Header -->
            <div class="d-grid text-caption text-grey font-weight-bold px-1 mb-1 align-center"
                style="display: grid; grid-template-columns: 24px 15ch 40px 40px; gap: 8px; width: max-content;">
                <div></div>
                <div>Name</div>
                <div class="text-right" title="Uptime while in combat (hover a value for the time)">Eff%</div>
                <div class="text-right" title="Uptime over the whole fight (hover a value for the time)">All%</div>
            </div>

            <!-- Sized to its columns, never stretched to the panel width -->
            <div class="status-list-container thin-scroll" style="width: max-content; max-width: 100%;">
                <div v-for="status in statuses" :key="status.key || status.id" class="d-grid align-center px-1 py-0 mb-1 rounded status-row"
                    style="display: grid; grid-template-columns: 24px 15ch 40px 40px; gap: 8px; font-size: 0.8rem;">
                    
                    <!-- Icon -->
                    <!-- Icon -->
                    <img width="24" height="24" :src='getStatusIconUrl(status.id)' class="rounded"
                        style="object-fit: cover; display: block;" loading="eager" decoding="async"
                        onerror="this.style.display='none'; this.nextElementSibling.style.display='block'" />
                    <v-icon icon="mdi-help-circle-outline" size="24" style="display: none"></v-icon>
                    
                    <!-- Name (show buff % when known, Vivace sub-stats only, or condition name) -->
                    <div class="text-truncate" :title="buildTooltip(status)" style="font-size: 0.9rem;">
                        <template v-if="status.isVivace">
                            <span v-if="status.maxPercent" class="text-blue-lighten-3">{{ shortName(status.name) }}</span>
                            <span class="text-purple-lighten-3" style="font-size: 0.8rem;">{{ buildVivaceShort(status) }}</span>
                        </template>
                        <template v-else>
                            <span v-if="status.maxPercent" class="text-blue-lighten-3">{{ shortName(status.name) }}</span>
                            <span v-else>{{ shortName(status.name) }}</span>
                        </template>
                    </div>
                    
                    <!-- Eff Coverage % -->
                    <div class="text-right font-weight-bold" :title="`Effective uptime: ${formatDuration(status.effectiveUptime)}`">
                        {{ Math.round(status.uptimeRatio * 100) }}%</div>

                    <!-- Overall Coverage % (uptimes as tooltips) -->
                    <div class="text-right text-grey-lighten-1" :title="`Overall uptime: ${formatDuration(status.uptime)}`">
                        {{ Math.round(status.overallRatio * 100) }}%</div>
                </div>
            </div>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'SelfStatusList',
    props: {
        statuses: {
            type: Array as () => any[],
            required: false // Can be null/undefined
        },
        title: {
            type: String,
            default: 'Self Statuses'
        },
        formatDuration: {
            type: Function,
            required: true
        },
        getStatusIconUrl: {
            type: Function,
            required: true
        }
    },
    setup(props) {
        const buildTooltip = (status: any) => {
            const lines: string[] = [];
            // Show original condition name when display name is a percentage
            if (status.maxPercent) {
                lines.push(`${status.condName || status.name} (${status.maxPercent}%)`);
            } else {
                lines.push(status.name);
            }
            lines.push(`ID: ${status.id}`);
            // Vivace sub-stats
            if (status.isVivace) {
                if (status.magicAttack) lines.push(`Magic Attack: +${status.magicAttack}`);
                if (status.alchemicDamage) lines.push(`Alchemic Damage: +${status.alchemicDamage}%`);
                if (status.magicCastingSpeed) lines.push(`Magic Casting Speed: +${status.magicCastingSpeed}%`);
                if (status.alchemyCastingSpeed) lines.push(`Alchemy Casting Speed: +${status.alchemyCastingSpeed}%`);
            }
            lines.push(`Eff%: ${Math.round(status.uptimeRatio * 100)}%`);
            lines.push(`All%: ${Math.round(status.overallRatio * 100)}%`);
            lines.push(`Eff Uptime: ${props.formatDuration(status.effectiveUptime)}`);
            lines.push(`All Uptime: ${props.formatDuration(status.uptime)}`);
            return lines.join('\n');
        };

        const buildVivaceShort = (status: any) => {
            const parts: string[] = [];
            if (status.magicAttack) parts.push(`MA:${status.magicAttack}`);
            if (status.alchemicDamage) parts.push(`AD:${status.alchemicDamage}%`);
            if (status.magicCastingSpeed) parts.push(`MCS:${status.magicCastingSpeed}%`);
            if (status.alchemyCastingSpeed) parts.push(`ACS:${status.alchemyCastingSpeed}%`);
            return parts.length > 0 ? `(${parts.join(' ')})` : '';
        };

        // At most 15 characters, spaces included (the full name is in the tooltip).
        const shortName = (name: string) => String(name ?? '').trim().slice(0, 15).trimEnd();

        return { buildTooltip, buildVivaceShort, shortName };
    }
});
</script>

<style scoped>
.status-list-container {
    max-height: 60vh;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.thin-scroll::-webkit-scrollbar {
    width: 6px;
}

.thin-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
}

.status-row:hover {
    background: rgba(255, 255, 255, 0.05);
}
</style>
